import React, { useState, useEffect } from "react";
import { Table, Modal, Button, notification, Input, Switch } from "antd";
import { baseApiUrl } from "../../services/api";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const BesoinAdmin = () => {
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [response, setResponse] = useState("");
  const [isRead, setIsRead] = useState(false);
  const [besoins, setBesoins] = useState([]);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isRepondreModalVisible, setIsRepondreModalVisible] = useState(false);
  const [viewingBesoin, setViewingBesoin] = useState(null);
  const [editingBesoin, setEditingBesoin] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteBesoin, setDeleteBesoin] = useState(null);
  const [filterNonLus, setFilterNonLus] = useState(false);

  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      message: message,
    });
  };

  const truncateText = (text, wordLimit) => {
    if (typeof text !== "undefined" && text !== null) {
      const words = text.split(" ");
      if (words.length > wordLimit) {
        return words.slice(0, wordLimit).join(" ") + "...";
      }
      return text;
    } else {
      return "";
    }
  };

  const fetchBesoins = async () => {
    try {
      // Adapter l'endpoint selon votre API
      const endpoint = filterNonLus ? "/besoins/non-lus" : "/besoins/liste";
      const response = await baseApiUrl.get(endpoint);

      if (response.status !== 200) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = response.data;

      if (!data || data.length === 0) {
        console.warn("Aucun besoin trouvé.");
      }

      setBesoins(data);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des besoins:",
        error.message || error
      );
      openNotificationWithIcon(
        "error",
        "Erreur lors de la récupération des besoins."
      );
    }
  };

  useEffect(() => {
    fetchBesoins();
  }, [filterNonLus]);

  const handleAddBesoin = async (e) => {
    e.preventDefault();

    if (!nom || !email || !message) {
      openNotificationWithIcon("error", "Veuillez remplir tous les champs obligatoires.");
      return;
    }

    try {
      const besoinData = {
        nom,
        email,
        message,
        isRead: false
      };

      const response = await baseApiUrl.post("/besoins", besoinData);
      const newBesoin = response.data;

      setBesoins([newBesoin, ...besoins]);

      openNotificationWithIcon("success", "Besoin ajouté avec succès.");
      fetchBesoins();
      setIsAddModalVisible(false);
    } catch (error) {
      openNotificationWithIcon("error", "Erreur lors de l'ajout du besoin.");
      console.error("Erreur détectée :", error.response?.data || error.message || error);
    }

    resetForm();
  };

  const handleEditBesoin = async (e) => {
    e.preventDefault();

    if (!editingBesoin) {
      openNotificationWithIcon("error", "Aucun besoin sélectionné pour la modification.");
      return;
    }

    if (!nom || !email || !message) {
      openNotificationWithIcon("error", "Veuillez remplir tous les champs obligatoires.");
      return;
    }

    try {
      // Préparation des données à envoyer
      const besoinData = {
        id: editingBesoin.id,
        nom,
        email,
        message,
        response: editingBesoin.response,
        responseDate: editingBesoin.responseDate,
        creationDate: editingBesoin.creationDate,
        isRead
      };

      // Mise à jour du besoin
      const response = await baseApiUrl.put(`/besoins/update/${editingBesoin.id}`, besoinData);

      if (response.status !== 200) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      // Mettre à jour l'état lu/non lu séparément pour s'assurer que ça fonctionne
      if (isRead !== editingBesoin.isRead) {
        await baseApiUrl.put(`/besoins/${editingBesoin.id}/marquer-lu`, {
          isRead: isRead
        });
      }

      const updatedBesoin = response.data;
      console.log("Besoin mis à jour:", updatedBesoin);

      setBesoins(
        besoins.map((besoin) => (besoin.id === updatedBesoin.id ? { ...updatedBesoin, isRead } : besoin))
      );

      openNotificationWithIcon("success", "Besoin modifié avec succès.");
      setIsEditModalVisible(false);
      await fetchBesoins();
    } catch (error) {
      console.error("Erreur lors de la modification du besoin:", error);
      console.error("Détails:", error.response?.data);
      openNotificationWithIcon("error", "Erreur lors de la modification du besoin.");
    }

    resetForm();
  };

  const handleMarquerLu = async (id, nouvelEtat) => {
    try {
      // Utiliser un endpoint spécifique pour marquer comme lu/non lu
      const response = await baseApiUrl.put(`/besoins/${id}/marquer-lu`, {
        isRead: nouvelEtat
      });

      if (response.status !== 200) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const updatedBesoin = response.data;

      setBesoins(
        besoins.map((besoin) => (besoin.id === updatedBesoin.id ? updatedBesoin : besoin))
      );

      openNotificationWithIcon("success", "Statut de lecture mis à jour.");
      await fetchBesoins();
    } catch (error) {
      console.error("Erreur lors de la mise à jour du statut:", error);
      openNotificationWithIcon("error", "Erreur lors de la mise à jour du statut.");
    }
  };

  const handleRepondre = async (e) => {
    e.preventDefault();

    if (!viewingBesoin) {
      openNotificationWithIcon("error", "Aucun besoin sélectionné pour la réponse.");
      return;
    }

    if (!response) {
      openNotificationWithIcon("error", "Veuillez entrer une réponse.");
      return;
    }

    try {
      // Utilisation de POST pour envoyer une réponse
      const reponseData = {
        besoinId: viewingBesoin.id,
        response: response
      };

      const apiResponse = await baseApiUrl.post(`/besoins/${viewingBesoin.id}/response`, reponseData);

      if (apiResponse.status !== 200 && apiResponse.status !== 201) {
        throw new Error(`Erreur HTTP: ${apiResponse.status}`);
      }

      const resultBesoin = apiResponse.data;

      setBesoins(
        besoins.map((besoin) => (besoin.id === resultBesoin.id ? resultBesoin : besoin))
      );

      openNotificationWithIcon("success", "Réponse envoyée avec succès.");
      setIsRepondreModalVisible(false);
      await fetchBesoins();
    } catch (error) {
      console.error("Erreur lors de l'envoi de la réponse:", error);
      openNotificationWithIcon("error", "Erreur lors de l'envoi de la réponse.");
    }

    setResponse("");
  };

  const handleEdit = (besoin) => {
    setViewingBesoin(besoin);
    setEditingBesoin(besoin);
    setNom(besoin.nom);
    setEmail(besoin.email);
    setMessage(besoin.message);
    setIsRead(besoin.isRead);

    setIsEditModalVisible(true);
  };

  const handleDeleteConfirm = (besoin) => {
    setDeleteBesoin(besoin);
    setDeleteModalVisible(true);
  };

  const handleDelete = async (idBesoin) => {
    try {
      const response = await baseApiUrl.delete(`/besoins/delete/${idBesoin}`);

      if (response.status !== 200 && response.status !== 204) {
        console.error(`Statut inattendu : ${response.status}`);
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      setBesoins((prevBesoins) =>
        prevBesoins.filter((besoin) => besoin.id !== idBesoin)
      );

      setDeleteModalVisible(false);
      setDeleteBesoin(null);

      openNotificationWithIcon("success", "Besoin supprimé avec succès.");
    } catch (error) {
      console.error("Erreur lors de la suppression :", error.message || error);
      openNotificationWithIcon(
        "error",
        "Une erreur est survenue lors de la suppression du besoin."
      );
    }
  };

  const resetForm = () => {
    setIsEditModalVisible(false);
    setIsAddModalVisible(false);
    setIsViewModalVisible(false);
    setIsRepondreModalVisible(false);
    setNom("");
    setEmail("");
    setMessage("");
    setResponse("");
    setIsRead(false);
    setEditingBesoin(null);
  };

  const handleView = (besoin) => {
    setViewingBesoin(besoin);
    setIsViewModalVisible(true);

    // Si le besoin n'est pas encore marqué comme lu, le marquer automatiquement
    if (!besoin.isRead) {
      handleMarquerLu(besoin.id, true);
    }
  };

  const handleOpenRepondreModal = (besoin) => {
    setViewingBesoin(besoin);
    setIsRepondreModalVisible(true);
  };

  const filteredBesoins = besoins.filter((besoin) => {
    const nomMatch = besoin.nom && besoin.nom.toLowerCase().includes(searchTerm.toLowerCase());
    const emailMatch = besoin.email && besoin.email.toLowerCase().includes(searchTerm.toLowerCase());
    const messageMatch = besoin.message && besoin.message.toLowerCase().includes(searchTerm.toLowerCase());
    return nomMatch || emailMatch || messageMatch;
  });

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-7 col-auto">
                <h3 className="page-title">Gestion des Besoins</h3>
                <div className="top-nav-search">
                  <input
                    className="form-control"
                    placeholder="Rechercher par nom, email ou message"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-sm-5 col">
                <Button
                  type="success"
                  onClick={() => {
                    resetForm();
                    setIsAddModalVisible(true);
                  }}
                  className="float-end mt-2 btn-success"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" /> {/* Icône ajoutée ici */}
                  Ajouter
                </Button>
                <div className="float-end mt-2 me-4">
                  <label className="me-2">Afficher seulement les non lus</label>
                  <Switch checked={filterNonLus} onChange={setFilterNonLus} />
                </div>
              </div>
            </div>
          </div>

          <Table
            rowKey="id"
            columns={[
              {
                title: "Nom",
                dataIndex: "nom",
                key: "nom",
              },
              {
                title: "Email",
                dataIndex: "email",
                key: "email",
              },
              {
                title: "Message",
                dataIndex: "message",
                key: "message",
                render: (text) => truncateText(text, 6),
              },
              {
                title: "État",
                dataIndex: "isRead",
                key: "isRead",
                render: (isRead) => (
                  isRead ?
                    <span className="badge bg-success">Lu</span> :
                    <span className="badge bg-warning">Non Lu</span>
                ),
              },
              {
                title: "A une réponse",
                dataIndex: "response",
                key: "response",
                render: (response) => (
                  response ?
                    <span className="badge bg-success">Oui</span> :
                    <span className="badge bg-secondary">Non</span>
                ),
              },
              {
                title: "Date de création",
                dataIndex: "creationDate",
                key: "creationDate",
                render: (creationDate) => {
                  if (creationDate) {
                    const date = new Date(creationDate);
                    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
                  }
                  return "N/A";
                },
              },
              {
                key: "actions",
                render: (text, record) => (
                  <div className="text-end">
                    <Button
                      className="ms-2 btn btn-sm bg-success-light"
                      onClick={() => {
                        resetForm();
                        handleView(record);
                      }}
                    >
                      Voir
                    </Button>
                    <Button
                      className="ms-2 btn btn-sm bg-info-light"
                      onClick={() => {
                        resetForm();
                        handleOpenRepondreModal(record);
                      }}
                    >
                      Répondre
                    </Button>
                    <Button
                      className="ms-2 btn btn-sm bg-primary-light"
                      onClick={() => {
                        resetForm();
                        handleEdit(record);
                      }}
                    >
                      Modifier
                    </Button>
                    <Button
                      className="ms-2 btn btn-sm bg-danger-light"
                      onClick={() => handleDeleteConfirm(record)}
                    >
                      Supprimer
                    </Button>
                  </div>
                ),
              },
            ]}
            dataSource={filteredBesoins}
          />

          {/* Modal d'ajout de besoin */}
          <Modal
            title="Ajouter un besoin"
            visible={isAddModalVisible}
            onCancel={() => setIsAddModalVisible(false)}
            width={800}
            footer={null}
          >
            <form onSubmit={handleAddBesoin}>
              <div className="row">
                <div className="col-sm-6 col-12">
                  <div className="form-group">
                    <label>Nom</label>
                    <input
                      type="text"
                      className="form-control"
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-sm-6 col-12">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Créer
                  </button>
                </div>
              </div>
            </form>
          </Modal>

          {/* Modal de modification de besoin */}
          <Modal
            title="Modifier un besoin"
            visible={isEditModalVisible}
            onCancel={() => setIsEditModalVisible(false)}
            width={800}
            footer={null}
          >
            <form onSubmit={handleEditBesoin}>
              <div className="row">
                <div className="col-sm-6 col-12">
                  <div className="form-group">
                    <label>Nom</label>
                    <input
                      type="text"
                      className="form-control"
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-sm-6 col-12">
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-6 col-12">
                  <div className="form-group">
                    <label>État</label>
                    <div className="mt-2">
                      <Switch
                        checked={isRead}
                        onChange={setIsRead}
                        checkedChildren="Lu"
                        unCheckedChildren="Non Lu"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    ></textarea>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Mettre à jour
                  </button>
                </div>
              </div>
            </form>
          </Modal>

          {/* Modal de confirmation de suppression */}
          <Modal
            title="Confirmer la suppression"
            visible={deleteModalVisible}
            onCancel={() => setDeleteModalVisible(false)}
            footer={[
              <Button key="back" onClick={() => setDeleteModalVisible(false)}>
                Annuler
              </Button>,
              <Button
                key="submit"
                type="primary"
                danger
                onClick={() => handleDelete(deleteBesoin.id)}
              >
                Supprimer
              </Button>,
            ]}
          >
            <p>
              Êtes-vous sûr de vouloir supprimer ce besoin ? Cette action est
              irréversible.
            </p>
          </Modal>

          {/* Modal pour afficher les détails */}
          <Modal
            title="Détails du besoin"
            visible={isViewModalVisible}
            onCancel={() => setIsViewModalVisible(false)}
            footer={[
              <Button key="close" onClick={() => setIsViewModalVisible(false)}>
                Fermer
              </Button>,
            ]}
          >
            {viewingBesoin && (
              <div>
                <div className="border rounded p-3 mb-3">
                  <h4>De: {viewingBesoin.nom}</h4>
                  <p className="text-muted">Email: {viewingBesoin.email}</p>
                  <div className="mb-2">
                    {viewingBesoin.isRead ?
                      <span className="badge bg-success ms-2">Lu</span> :
                      <span className="badge bg-warning ms-2">Non Lu</span>
                    }
                  </div>
                  <p className="text-muted">
                    Créé le: {new Date(viewingBesoin.creationDate).toLocaleDateString()} à {new Date(viewingBesoin.creationDate).toLocaleTimeString()}
                  </p>
                  <h5>Message:</h5>
                  <p style={{ whiteSpace: 'pre-line' }}>{viewingBesoin.message}</p>
                </div>

                {viewingBesoin.response && (
                  <div className="border rounded p-3 bg-light">
                    <h5>Réponse:</h5>
                    <p style={{ whiteSpace: 'pre-line' }}>{viewingBesoin.response}</p>
                    <p className="text-muted">
                      Répondu le: {new Date(viewingBesoin.responseDate).toLocaleDateString()} à {new Date(viewingBesoin.responseDate).toLocaleTimeString()}
                    </p>
                  </div>
                )}

                {!viewingBesoin.response && (
                  <div className="text-center mt-3">
                    <Button
                      type="primary"
                      onClick={() => {
                        setIsViewModalVisible(false);
                        setIsRepondreModalVisible(true);
                      }}
                    >
                      Répondre à ce besoin
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Modal>

          {/* Modal pour répondre à un besoin */}
          <Modal
            title="Répondre au besoin"
            visible={isRepondreModalVisible}
            onCancel={() => setIsRepondreModalVisible(false)}
            width={800}
            footer={null}
          >
            {viewingBesoin && (
              <div>
                <div className="border rounded p-3 mb-3">
                  <h5>De: {viewingBesoin.nom}</h5>
                  <p>Email: {viewingBesoin.email}</p>
                  <h6>Message:</h6>
                  <p style={{ whiteSpace: 'pre-line' }}>{viewingBesoin.message}</p>
                </div>

                <form onSubmit={handleRepondre}>
                  <div className="form-group">
                    <label>Votre réponse</label>
                    <textarea
                      className="form-control"
                      rows="6"
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="Entrez votre réponse..."
                    ></textarea>
                  </div>

                  <div className="mt-3">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!response}
                    >
                      Envoyer la réponse
                    </button>
                  </div>
                </form>
              </div>
            )}
          </Modal>
        </div>
      </div>
    </>
  );
};

export default BesoinAdmin;
